import {Component, EventEmitter, Injector, Output, ViewChild, OnInit, OnDestroy} from '@angular/core';
import { AppConsts } from '@shared/AppConsts';
import { AppComponentBase } from '@axilla/axilla-shared';
import {
    CurrentUserProfileEditDto,
    SettingScopes,
    ProfileServiceProxy,
    UpdateGoogleAuthenticatorKeyOutput,
    SendVerificationSmsInputDto,
    ChangeUserLanguageDto,
    UblServiceProxy, UserServiceProxy, VirtualBankAccountDto, CreateOrUpdateVirtualBankDto, ICreateOrUpdateVirtualBankDto,
} from '@shared/service-proxies/service-proxies';

import { ModalDirective } from 'ngx-bootstrap/modal';
import { SmsVerificationModalComponent } from './sms-verification-modal.component';
import * as _ from 'lodash';
import {BasePermissionDefinition} from '@shared/permission/base-permission-definition';
import {FormBuilder, FormGroup, NgForm} from '@angular/forms';
import {BillingInfoValidator} from '@app/shared/common/billing-information/billing-information.component';
import {distinctUntilChanged, pairwise, startWith, finalize, take, concatMap} from 'rxjs/operators';
import {Subscription} from 'rxjs';

@Component({
    selector: 'mySettingsModal',
    templateUrl: './my-settings-modal.component.html'
})
export class MySettingsModalComponent extends AppComponentBase implements OnInit, OnDestroy {
    protected readonly BasePermissionDefinition = BasePermissionDefinition;

    @ViewChild('mySettingsModal', { static: true }) modal: ModalDirective;
    @ViewChild('smsVerificationModal', { static: false }) smsVerificationModal: SmsVerificationModalComponent;
    @Output() modalSave: EventEmitter<any> = new EventEmitter<any>();

    public active = false;
    public saving = false;
    public isGoogleAuthenticatorEnabled = false;
    public isPhoneNumberConfirmed: boolean;
    public smsEnabled: boolean;
    public user: CurrentUserProfileEditDto;
    public showTimezoneSelection: boolean = abp.clock.provider.supportsMultipleTimezone;
    public canChangeUserName: boolean;
    public defaultTimezoneScope: SettingScopes = SettingScopes.User;
    private _initialTimezone: string = undefined;
    public savedPhoneNumber: string;
    public newPhoneNumber: string;
    isMultiTenancyEnabled: boolean = this.multiTenancy.isEnabled;
    isTwoFactorLoginEnabledForApplication = false;

    languages: abp.localization.ILanguageInfo[];
    currentLanguage: abp.localization.ILanguageInfo;
    fullLanguages: abp.localization.ILanguageInfo[];
    invoiceLanguages: abp.localization.ILanguageInfo;

    private _isLanguageChange = false;

    private _cultureName = 'Abp.Localization.CultureName';
    private _invoiceLanguage = 'UblSetting.InvoiceLanguage';
    private _invoiceLanguages = ['nl', 'nb', 'en'];

    vatRates: any;
    vatSelected: any = {};
    vatExplanation: any = {};
    userVat: string;

    vatExplanationSelected: any = {};
    vatExplanationGrouped: any;

    billingInfoForm!: FormGroup;

    @ViewChild('billingInformation') public billingInformationForm: NgForm;
    billingInfoValidator?: BillingInfoValidator;

    billInfo: VirtualBankAccountDto = new VirtualBankAccountDto();

    private subscriptions: Subscription[] = [];

    constructor(
        injector: Injector,
        private _profileService: ProfileServiceProxy,
        private _ublService: UblServiceProxy,
        private _userServiceProxy: UserServiceProxy
        , private fb: FormBuilder
    ) {
        super(injector);
    }

    ngOnInit(): void {
        this.isTwoFactorLoginEnabledForApplication =
            abp.setting.getBoolean('Abp.Zero.UserManagement.TwoFactorLogin.IsEnabled');

        this.initLanguages();
        //this._getVat();
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }

    private _initBillingInfo() {
        // Ensure billingInfoValidator is an instance of BillingInfoValidator
        if (!(this.billingInfoValidator instanceof BillingInfoValidator)) {
            this.billingInfoValidator = new BillingInfoValidator(this.billingInfoValidator);
        }

        this.billingInfoForm = this.fb.group({
            visitorLegalName: ['', this.billingInfoValidator.getValidator('requiredVisitorLegalName', 'requiredVisitorBlock')],
            visitorContactName: ['', this.billingInfoValidator.getValidator('requiredVisitorContactName', 'requiredVisitorBlock')],
            visitorAddress: ['', this.billingInfoValidator.getValidator('requiredVisitorAddress', 'requiredVisitorBlock')],
            visitorAddressHouseNumber: ['', this.billingInfoValidator.getValidator('requiredVisitorAddressHouseNumber', 'requiredVisitorBlock')],
            visitorAddressPostcode: ['', this.billingInfoValidator.getValidator('requiredVisitorAddressPostcode', 'requiredVisitorBlock')],
            visitorAddressCity: ['', this.billingInfoValidator.getValidator('requiredVisitorAddressCity', 'requiredVisitorBlock')],
            visitorAddressCountry: ['', this.billingInfoValidator.getValidator('requiredVisitorAddressCountry', 'requiredVisitorBlock')],

            invoiceContactName: ['', this.billingInfoValidator.getValidator('requiredInvoiceContactName', 'requiredInvoiceBlock')],
            invoiceAddress: ['', this.billingInfoValidator.getValidator('requiredInvoiceAddress', 'requiredInvoiceBlock')],
            invoiceAddressHouseNumber: ['', this.billingInfoValidator.getValidator('requiredInvoiceAddressHouseNumber', 'requiredInvoiceBlock')],
            invoiceAddressPostcode: ['', this.billingInfoValidator.getValidator('requiredInvoiceAddressPostcode', 'requiredInvoiceBlock')],
            invoiceAddressCity: ['', this.billingInfoValidator.getValidator('requiredInvoiceAddressCity', 'requiredInvoiceBlock')],
            invoiceAddressCountry: ['', this.billingInfoValidator.getValidator('requiredInvoiceAddressCountry', 'requiredInvoiceBlock')],

            deliveryContactName: ['', this.billingInfoValidator.getValidator('requiredDeliveryContactName', 'requiredDeliveryBlock')],
            deliveryAddress: ['', this.billingInfoValidator.getValidator('requiredDeliveryAddress', 'requiredDeliveryBlock')],
            deliveryAddressHouseNumber: ['', this.billingInfoValidator.getValidator('requiredDeliveryAddressHouseNumber', 'requiredDeliveryBlock')],
            deliveryAddressPostcode: ['', this.billingInfoValidator.getValidator('requiredDeliveryAddressPostcode', 'requiredDeliveryBlock')],
            deliveryAddressCity: ['', this.billingInfoValidator.getValidator('requiredDeliveryAddressCity', 'requiredDeliveryBlock')],
            deliveryAddressCountry: ['', this.billingInfoValidator.getValidator('requiredDeliveryAddressCountry', 'requiredDeliveryBlock')],

            contactName: ['', this.billingInfoValidator.getValidator('requiredContactName', 'requiredSearchBlock')],
            phoneNumber: ['', this.billingInfoValidator.getValidator('requiredPhoneNumber', 'requiredSearchBlock')],
            phoneNumberSecondary: ['', this.billingInfoValidator.getValidator('requiredPhoneNumberSecondary', 'requiredSearchBlock')],
            invoiceEAddress: ['', this.billingInfoValidator.getValidator('requiredInvoiceEAddress', 'requiredSearchBlock')],
            invoiceRememberEInvoiceAddress: ['', this.billingInfoValidator.getValidator('requiredInvoiceRememberEInvoiceAddress', 'requiredSearchBlock')],

            accountBankName: ['', this.billingInfoValidator.getValidator('requiredAccountBankName', 'requiredCompanyDataBlock')],
            accountNumber: ['', this.billingInfoValidator.getValidator('requiredAccountNumber', 'requiredCompanyDataBlock')],
            registrationNo: ['', this.billingInfoValidator.getValidator('requiredRegistrationNo', 'requiredCompanyDataBlock')],
            taxVatNo: ['', this.billingInfoValidator.getValidator('requiredTaxVatNo', 'requiredCompanyDataBlock')],
        });

        this.setupVisitorValueChangeSubscriptions();
        //this.setupInvoiceValueChangeSubscriptions();
        //this.setupDeliveryValueChangeSubscriptions();
    }

    private _mapToVirtualBank() {
        const data = this.billingInfoForm.value;

        this.billInfo.contactName = data.contactName;
        this.billInfo.legalName = data.visitorLegalName;
        this.billInfo.address = data.visitorAddress;
        this.billInfo.addressHouseNumber = data.visitorAddressHouseNumber;
        this.billInfo.addressPostcode = data.visitorAddressPostcode;
        this.billInfo.addressCity = data.visitorAddressCity;
        this.billInfo.addressCountry = data.visitorAddressCountry;
        this.billInfo.invoiceContactName = data.invoiceContactName;
        this.billInfo.invoiceAddress = data.invoiceAddress;
        this.billInfo.invoiceAddressHouseNumber = data.invoiceAddressHouseNumber;
        this.billInfo.invoiceAddressPostcode = data.invoiceAddressPostcode;
        this.billInfo.invoiceAddressCity = data.invoiceAddressCity;
        this.billInfo.invoiceAddressCountry = data.invoiceAddressCountry;
        this.billInfo.deliveryContactName = data.deliveryContactName;
        this.billInfo.deliveryAddress = data.deliveryAddress;
        this.billInfo.deliveryAddressHouseNumber = data.deliveryAddressHouseNumber;
        this.billInfo.deliveryAddressPostcode = data.deliveryAddressPostcode;
        this.billInfo.deliveryAddressCity = data.deliveryAddressCity;
        this.billInfo.deliveryAddressCountry = data.deliveryAddressCountry;
        this.billInfo.phoneNumber = data.phoneNumber;
        this.billInfo.phoneNumberSecondary = data.phoneNumberSecondary;
        this.billInfo.invoiceEAddress = data.invoiceEAddress;
        this.billInfo.invoiceRememberEInvoiceAddress = data.invoiceRememberEInvoiceAddress;
        this.billInfo.accountBankName = data.accountBankName;
        this.billInfo.accountNumber = data.accountNumber;
        this.billInfo.registrationNo = data.registrationNo;
        this.billInfo.taxVatNo = data.taxVatNo;

        return this.billInfo;
    }

    private _setupBillingForm() {
        const updates: { [key: string]: any } = {};

        // Contact Information
        if (this.billInfo.contactName) {
            updates['contactName'] = this.billInfo.contactName;
            updates['visitorContactName'] = this.billInfo.contactName;
        }
        if (this.billInfo.invoiceEAddress) {
            updates['invoiceEAddress'] = this.billInfo.invoiceEAddress;
        }
        if (this.billInfo.invoiceRememberEInvoiceAddress) {
            updates['invoiceRememberEInvoiceAddress'] = this.billInfo.invoiceRememberEInvoiceAddress;
        }
        if (this.billInfo.phoneNumber) {
            updates['phoneNumber'] = this.billInfo.phoneNumber;
        }
        if (this.billInfo.phoneNumberSecondary) {
            updates['phoneNumberSecondary'] = this.billInfo.phoneNumberSecondary;
        }
        if (this.billInfo.accountBankName) {
            updates['accountBankName'] = this.billInfo.accountBankName;
        }
        if (this.billInfo.accountNumber) {
            updates['accountNumber'] = this.billInfo.accountNumber;
        }
        if (this.billInfo.registrationNo) {
            updates['registrationNo'] = this.billInfo.registrationNo;
        }
        if (this.billInfo.taxVatNo) {
            updates['taxVatNo'] = this.billInfo.taxVatNo;
        }
        if (this.billInfo.legalName) {
            updates['visitorLegalName'] = this.billInfo.legalName;
        }
        if (this.billInfo.address) {
            updates['visitorAddress'] = this.billInfo.address;
        }
        if (this.billInfo.addressHouseNumber) {
            updates['visitorAddressHouseNumber'] = this.billInfo.addressHouseNumber;
        }
        if (this.billInfo.addressPostcode) {
            updates['visitorAddressPostcode'] = this.billInfo.addressPostcode;
        }
        if (this.billInfo.addressCity) {
            updates['visitorAddressCity'] = this.billInfo.addressCity;
        }
        if (this.billInfo.addressCountry) {
            updates['visitorAddressCountry'] = this.billInfo.addressCountry;
        }
        if (this.billInfo.invoiceContactName) {
            updates['invoiceContactName'] = this.billInfo.invoiceContactName;
        }
        if (this.billInfo.invoiceAddress) {
            updates['invoiceAddress'] = this.billInfo.invoiceAddress;
        }
        if (this.billInfo.invoiceAddressHouseNumber) {
            updates['invoiceAddressHouseNumber'] = this.billInfo.invoiceAddressHouseNumber;
        }
        if (this.billInfo.invoiceAddressPostcode) {
            updates['invoiceAddressPostcode'] = this.billInfo.invoiceAddressPostcode;
        }
        if (this.billInfo.invoiceAddressCity) {
            updates['invoiceAddressCity'] = this.billInfo.invoiceAddressCity;
        }
        if (this.billInfo.invoiceAddressCountry) {
            updates['invoiceAddressCountry'] = this.billInfo.invoiceAddressCountry;
        }
        if (this.billInfo.deliveryContactName) {
            updates['deliveryContactName'] = this.billInfo.deliveryContactName;
        }
        if (this.billInfo.deliveryAddress) {
            updates['deliveryAddress'] = this.billInfo.deliveryAddress;
        }
        if (this.billInfo.deliveryAddressHouseNumber) {
            updates['deliveryAddressHouseNumber'] = this.billInfo.deliveryAddressHouseNumber;
        }
        if (this.billInfo.deliveryAddressPostcode) {
            updates['deliveryAddressPostcode'] = this.billInfo.deliveryAddressPostcode;
        }
        if (this.billInfo.deliveryAddressCity) {
            updates['deliveryAddressCity'] = this.billInfo.deliveryAddressCity;
        }
        if (this.billInfo.deliveryAddressCountry) {
            updates['deliveryAddressCountry'] = this.billInfo.deliveryAddressCountry;
        }

        this.billingInfoForm.patchValue(updates, { emitEvent: false });
    }

    private setupDeliveryValueChangeSubscriptions(): void {
        const fieldMappings: { [key: string]: string[] } = {
            deliveryContactName: ['contactName', 'visitorContactName', 'invoiceContactName'],
            deliveryAddress: ['visitorAddress', 'invoiceAddress'],
            deliveryAddressHouseNumber: ['visitorAddressHouseNumber', 'invoiceAddressHouseNumber'],
            deliveryAddressPostcode: ['visitorAddressPostcode', 'invoiceAddressPostcode'],
            deliveryAddressCity: ['visitorAddressCity', 'invoiceAddressCity'],
            deliveryAddressCountry: ['visitorAddressCountry', 'invoiceAddressCountry']
        };

        Object.keys(fieldMappings).forEach(sourceField => {
            const sub = this.billingInfoForm.get(sourceField)?.valueChanges
                .pipe(
                    distinctUntilChanged()) // Prevent duplicate triggers
                .subscribe(value => {
                    const updates: { [key: string]: any } = {};
                    const oldValue = value.slice(0, value.length - 1);

                    fieldMappings[sourceField].forEach(targetField => {
                        const targetValue = this.billingInfoForm.get(targetField)?.value;

                        if (value && !targetValue) {
                            // If source has a value but target is empty, copy value
                            updates[targetField] = value;
                        } else if (oldValue === targetValue) {
                            updates[targetField] = value;
                        } else {
                            return;
                        }
                    });

                    if (Object.keys(updates).length > 0) {
                        this.billingInfoForm.patchValue(updates, { emitEvent: false });
                    }
                });

            if (sub) {
                this.subscriptions.push(sub);
            }
        });
    }
    private setupInvoiceValueChangeSubscriptions(): void {
        const fieldMappings: { [key: string]: string[] } = {
            invoiceContactName: ['contactName', 'visitorContactName', 'deliveryContactName'],
            invoiceAddress: ['visitorAddress', 'deliveryAddress'],
            invoiceAddressHouseNumber: ['visitorAddressHouseNumber', 'deliveryAddressHouseNumber'],
            invoiceAddressPostcode: ['visitorAddressPostcode', 'deliveryAddressPostcode'],
            invoiceAddressCity: ['visitorAddressCity', 'deliveryAddressCity'],
            invoiceAddressCountry: ['visitorAddressCountry', 'deliveryAddressCountry']
        };

        Object.keys(fieldMappings).forEach(sourceField => {
            const sub = this.billingInfoForm.get(sourceField)?.valueChanges
                .pipe(distinctUntilChanged()) // Prevent duplicate triggers
                .subscribe(value => {
                    const updates: { [key: string]: any } = {};
                    const oldValue = value.slice(0, value.length - 1);

                    fieldMappings[sourceField].forEach(targetField => {
                        const targetValue = this.billingInfoForm.get(targetField)?.value;

                        if (value && !targetValue) {
                            // If source has a value but target is empty, copy value
                            updates[targetField] = value;
                        } else if (oldValue === targetValue) {
                            updates[targetField] = value;
                        } else {
                            return;
                        }
                    });

                    if (Object.keys(updates).length > 0) {
                        this.billingInfoForm.patchValue(updates, { emitEvent: false });
                    }
                });

            if (sub) {
                this.subscriptions.push(sub);
            }
        });
    }
    private setupVisitorValueChangeSubscriptions(): void {
        const fieldMappings: { [key: string]: string[] } = {
            visitorContactName: ['contactName', 'invoiceContactName', 'deliveryContactName'],
            visitorAddress: ['invoiceAddress', 'deliveryAddress'],
            visitorAddressHouseNumber: ['invoiceAddressHouseNumber', 'deliveryAddressHouseNumber'],
            visitorAddressPostcode: ['invoiceAddressPostcode', 'deliveryAddressPostcode'],
            visitorAddressCity: ['invoiceAddressCity', 'deliveryAddressCity'],
            visitorAddressCountry: ['invoiceAddressCountry', 'deliveryAddressCountry']
        };

        Object.keys(fieldMappings).forEach(sourceField => {
            const sub = this.billingInfoForm.get(sourceField)?.valueChanges
                .pipe(
                    startWith(this.billingInfoForm.get(sourceField)?.value)
                    , distinctUntilChanged()
                    , pairwise()) // Prevent duplicate triggers
                .subscribe(([oldValue, value]) => {
                    console.log(oldValue, value);
                    const direction = value > oldValue ? 1 : -1;

                    const updates: { [key: string]: any } = {};

                    fieldMappings[sourceField].forEach(targetField => {
                        const targetValue = this.billingInfoForm.get(targetField)?.value;

                        if (direction === -1 && oldValue !== targetValue) {
                            return;
                        }

                        if ((value && !targetValue) || direction === -1) {
                            // If source has a value but target is empty, copy value
                            updates[targetField] = value;
                        } else if (oldValue === targetValue) {
                            updates[targetField] = value;
                        } else {
                            return;
                        }
                    });

                    if (Object.keys(updates).length > 0) {
                        this.billingInfoForm.patchValue(updates, { emitEvent: false });
                    }
                });

            if (sub) {
                this.subscriptions.push(sub);
            }
        });
    }

    getUserVba() {
        const userId = abp.session.userId;
        this._userServiceProxy.getOwnVirtualBankAccount(userId)
            .subscribe((result: VirtualBankAccountDto) => {
                this.billInfo = result;
                this._setupBillingForm();
            });
    }

    initLanguages(): void {
        this.fullLanguages = _.filter(this.localization.languages, l => {
            const index = _.findIndex(this._invoiceLanguages, value => {
                if (_.startsWith(l.name, value)) {
                    return true;
                }
                return false;
            });

            return index !== -1;
        });
        const filtered = _.filter(this.localization.languages, l => (l).isDisabled === false);
        this.languages = filtered.map(x => Object.assign({}, x));
        this.currentLanguage = Object.assign({}, this.localization.currentLanguage);

        const invLang = this._getInvoiceLanguage();
        const invLanguageIndex = _.findIndex(this.languages, i => i.name === invLang);
        this.invoiceLanguages = Object.assign({}, this.languages[invLanguageIndex]);
    }

    show(): void {
        this.active = true;

        if (this.permission.isGranted(BasePermissionDefinition.User.ProfileManagement._)) {
            this._initBillingInfo();
            this.getUserVba();
        }

        this._profileService
            .getCurrentUserProfileForEdit()
            .subscribe((result) => {
                this.smsEnabled = this.setting.getBoolean('App.UserManagement.SmsVerificationEnabled');
                this.user = result;
                this._initialTimezone = result.timezone;
                this.canChangeUserName = this.user.userName !== AppConsts.userManagement.defaultAdminUserName;
                this.modal.show();
                this.isGoogleAuthenticatorEnabled = result.isGoogleAuthenticatorEnabled;
                this.isPhoneNumberConfirmed = result.isPhoneNumberConfirmed;
                this.savedPhoneNumber = result.phoneNumber;
        });
    }

    updateQrCodeSetupImageUrl(): void {
        this._profileService.updateGoogleAuthenticatorKey().subscribe((result: UpdateGoogleAuthenticatorKeyOutput) => {
            this.user.qrCodeSetupImageUrl = result.qrCodeSetupImageUrl;
            this.isGoogleAuthenticatorEnabled = true;
        });
    }

    disableGoogleAuthenticator(): void {
        this._profileService.disableGoogleAuthenticator().subscribe(() => {
            this.isGoogleAuthenticatorEnabled = false;
        });
    }

    smsVerify(): void {
        let input = new SendVerificationSmsInputDto();
        input.phoneNumber = this.user.phoneNumber;
        this._profileService.sendVerificationSms(input)
            .subscribe(() => {
                this.smsVerificationModal.show();
            });
    }

    changePhoneNumberToVerified(): void {
        this.isPhoneNumberConfirmed = true;
        this.savedPhoneNumber = this.user.phoneNumber;
    }

    onShown(): void {
        document.getElementById('Name').focus();
    }

    close(): void {
        this.active = false;
        this.modal.hide();
    }

    save(): void {
        this.saving = true;

        debugger;

        if (this._checkCurrentLanguages()) {
            this.changeLanguage(this.currentLanguage.name);
            this._isLanguageChange = true;
        }

        const formValue = this._mapToVirtualBank();
        let input = formValue as ICreateOrUpdateVirtualBankDto;
        this._userServiceProxy.updateVirtualBankAccount(new CreateOrUpdateVirtualBankDto(input))
            .pipe(
                concatMap(() => this._profileService.updateCurrentUserProfile(this.user)), // Chain the second call
                finalize(() => { this.saving = false; })
            )
            .subscribe(() => {
                this.appSession.user.name = this.user.name;
                this.appSession.user.surname = this.user.surname;
                this.appSession.user.userName = this.user.userName;
                this.appSession.user.emailAddress = this.user.emailAddress;
                this.notify.success(this.l('SavedSuccessfully'));
                this.modalSave.emit(null);

                if (abp.clock.provider.supportsMultipleTimezone && this._initialTimezone !== this.user.timezone) {
                    this.message.info(this.l('TimeZoneSettingChangedRefreshPageNotification')).then(() => {
                        window.location.reload();
                    });
                }

                if (this._isLanguageChange) {
                    this._isLanguageChange = false;
                    location.reload();
                }

                this.close();
            });
    }

    changeLanguage(languageName: string): void {
        const input = new ChangeUserLanguageDto();
        input.languageName = languageName;

        this._profileService
            .changeLanguage(input)
            .subscribe(() => {
                abp.utils.setCookieValue(
                    this._cultureName,
                    languageName,
                    new Date(new Date().getTime() + 5 * 365 * 86400000), //5 year
                    abp.appPath
                );
            });
    }

    private _getUserInvoiceLanguage(): void {
        const invLang = this._getInvoiceLanguage();

        const invLanguageIndex = _.findIndex(this.languages, i => i.name === invLang);
        this.invoiceLanguages = Object.assign({}, this.languages[invLanguageIndex]);
    }

    private _getCurrentLanguage(source: string): string {
        return abp.utils.getCookieValue(source);
    }

    private _getInvoiceLanguage(): string {
        return abp.setting.get(this._invoiceLanguage);
    }

    private _checkInvoiceLanguages(): boolean {
        const isInvoiceLanguages = this.invoiceLanguages.name !== this._getInvoiceLanguage();
        return isInvoiceLanguages;
    }

    private _checkCurrentLanguages(): boolean {
        const isCurrentLanguage = this.currentLanguage.name !== this._getCurrentLanguage(this._cultureName);
        return isCurrentLanguage;
    }

    private _checkLanguages(): boolean {
        return this._checkInvoiceLanguages() || this._checkCurrentLanguages();
    }

    get showVat() {
        return false;
        return abp.features.isEnabled('App.Beta.Profile.Vat');
    }

}
